import { useEffect, type ReactNode } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { useAuthStore, selectIsAuthenticated } from "@/stores/auth";
import { useAuthModalStore } from "@/stores/ui";
import { useThemeStore, resolveEffective } from "@/stores/theme";
import { Marketing } from "@/pages/Marketing";
import { AppLayout } from "@/pages/AppLayout";
import { FreeView } from "@/pages/FreeView";
import { StylesView } from "@/pages/StylesView";
import { WorksView } from "@/pages/WorksView";
import { AssetsView } from "@/pages/AssetsView";
import { JobsView } from "@/pages/JobsView";
import { BillingView } from "@/pages/BillingView";
import { AdminView } from "@/pages/AdminView";
import { AuthModal } from "@/components/auth/AuthModal";
import { SamplesModal } from "@/components/marketing/SamplesModal";
import { Toaster } from "@/components/ui/toast";
import { Lightbox } from "@/components/shared/Lightbox";
import { RouteErrorBoundary } from "@/components/RouteErrorBoundary";

// Per-route wrapper: a crash inside one view falls into its own boundary so
// shell + nav + other routes keep working. "重试" resets the boundary
// without reloading the tab.
const guarded = (name: string, node: ReactNode) => (
  <RouteErrorBoundary routeName={name}>{node}</RouteErrorBoundary>
);

function App() {
  const isAuthed = useAuthStore(selectIsAuthenticated);
  const closeAuthModal = useAuthModalStore((s) => s.close);
  const themeMode = useThemeStore((s) => s.mode);

  // Safety net: whenever the session lights up, force the auth modal closed.
  // Avoids the case where a half-stuck Radix overlay paints over the shell.
  useEffect(() => {
    if (isAuthed) closeAuthModal();
  }, [isAuthed, closeAuthModal]);

  // Apply theme class whenever mode changes. The anti-FOUC inline script in
  // index.html handled the very first paint; this keeps things in sync when
  // the user picks a different mode at runtime.
  useEffect(() => {
    document.documentElement.classList.toggle(
      "dark",
      resolveEffective(themeMode) === "dark",
    );
  }, [themeMode]);

  // When mode is "system", follow the OS preference live.
  useEffect(() => {
    if (themeMode !== "system" || typeof matchMedia === "undefined") return;
    const mq = matchMedia("(prefers-color-scheme: dark)");
    const handler = () =>
      document.documentElement.classList.toggle("dark", mq.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [themeMode]);

  return (
    <>
      <Routes>
        {!isAuthed ? (
          <>
            <Route path="/" element={<Marketing />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </>
        ) : (
          <Route element={<AppLayout />}>
            <Route path="/" element={<Navigate to="/free" replace />} />
            <Route path="/free" element={guarded("创作台", <FreeView />)} />
            <Route path="/ecommerce" element={<Navigate to="/free" replace />} />
            <Route path="/styles" element={guarded("风格广场", <StylesView />)} />
            <Route path="/works" element={guarded("我的作品", <WorksView />)} />
            <Route path="/assets" element={guarded("素材库", <AssetsView />)} />
            <Route path="/jobs" element={guarded("任务", <JobsView />)} />
            <Route path="/billing" element={guarded("点数流水", <BillingView />)} />
            <Route path="/admin" element={guarded("后台数据", <AdminView />)} />
            <Route path="*" element={<Navigate to="/free" replace />} />
          </Route>
        )}
      </Routes>
      <AuthModal />
      <SamplesModal />
      <Lightbox />
      <Toaster />
    </>
  );
}

export default App;
